import { useId, useState } from "react"
import { useNavigate } from "react-router-dom"

import { Alert, App, Button, Flex, Form, Input, Skeleton } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { useTranslation } from "@/hooks/useTranslation"
import { LoadErrorEmptyTable } from "@/molecules/EmptyTable"
import {
  useAgentPlatform,
  useAgentPlatformConnections
} from "@/pages/DeploymentsPage/application/useAgentPlatform"
import {
  DEFAULT_AGENTCORE_CODE_ACCESS_INPUT,
  DEFAULT_AGENTCORE_SETTINGS,
  getAgentCoreSettingsRequest,
  getStackCodeAccess,
  isAgentCoreSettingsValid,
  isRoleArn
} from "@/pages/DeploymentsPage/application/useAgentPlatform.selector"
import {
  CloudConnectionDemoHint,
  CloudConnectionFields,
  CloudConnectionUnavailable,
  getCloudConnectionStep
} from "@/pages/DeploymentsPage/components/molecules/CloudConnectionStatus"
import { AGENTCORE_NAME } from "./AgentCoreCloudConnection.constants"
import { AgentCoreCodeAccessFields } from "./AgentCoreCodeAccessFields"
import { AgentCoreSettingsFields } from "./AgentCoreSettingsFields"
import { AgentCoreTemplate } from "./AgentCoreTemplate"
import { getFieldStatus } from "./helpers"
import { useAgentCoreTemplate } from "./useAgentCoreTemplate"

import type { AgentCoreSettings } from "@/api/useDeployments.types"
import type { StepItem } from "@/atoms"

// The agentless connections of the AWS accounts, in the AgentCore tab: Kodem
// discovers each account's AgentCore agents and, when allowed, enforces the AI
// policies on them. Every account is connected with these steps; the connected
// ones are on the deployments list, their settings at the end of their rows.
// Nothing is read until the method is selected.
export const useAgentCoreCloudConnection = (
  enabled: boolean
): { steps: StepItem[]; content?: React.ReactNode } => {
  const { t } = useTranslation("translation", {
    keyPrefix: "deployments.agentCore.cloud"
  })
  const { t: tConnection } = useTranslation("translation", {
    keyPrefix: "deployments.cloudConnection"
  })
  const { t: tRoot } = useTranslation()
  const { message } = App.useApp()
  const navigate = useNavigate()
  const { isLoading, isLoadError, isUnavailable, refetch } =
    useAgentPlatformConnections("agentcore", enabled)
  const platform = useAgentPlatform("agentcore")
  const template = useAgentCoreTemplate(platform.preInstall)
  const [settings, setSettings] = useState<AgentCoreSettings>(
    DEFAULT_AGENTCORE_SETTINGS
  )
  const [roleArn, setRoleArn] = useState("")
  const roleArnId = useId()
  const [codeAccess, setCodeAccess] = useState(
    DEFAULT_AGENTCORE_CODE_ACCESS_INPUT
  )

  if (!enabled) return { steps: [] }

  // Not a step of the user's: the state of the connection itself
  if (isLoading)
    return { steps: [], content: <Skeleton active paragraph={{ rows: 4 }} /> }

  if (isLoadError)
    return { steps: [], content: <LoadErrorEmptyTable onRetry={refetch} /> }

  if (isUnavailable)
    return { steps: [], content: <CloudConnectionUnavailable /> }

  const settingsValid = isAgentCoreSettingsValid(settings)
  const roleArnValid = isRoleArn(roleArn)

  const onSettingsChange = (next: AgentCoreSettings) => {
    setSettings(next)
    template.reset()
  }

  const onConnect = async () => {
    const connected = await platform.connect({
      roleArn: roleArn.trim(),
      settings: getAgentCoreSettingsRequest(settings)
    })

    if (!connected) return

    message.success(tConnection("connected", { name: AGENTCORE_NAME }))
    navigate(`/${NAMES_ROUTES.DEPLOYMENTS.ROOT}`)
  }

  const connectStep = getCloudConnectionStep(
    t("steps.connectPrefix"),
    t("steps.connect"),
    <Flex vertical gap={12}>
      <CloudConnectionFields>
        <Form.Item
          label={t("fields.roleArn.label")}
          help={!!roleArn.trim() && !roleArnValid && t("fields.roleArn.error")}
          validateStatus={getFieldStatus(!!roleArn.trim(), roleArnValid)}
          hasFeedback
          htmlFor={roleArnId}
        >
          <Input
            id={roleArnId}
            value={roleArn}
            placeholder={t("fields.roleArn.placeholder")}
            onChange={(e) => setRoleArn(e.target.value)}
          />
        </Form.Item>
      </CloudConnectionFields>
      <Flex gap={8} align="center">
        <Button
          type="primary"
          loading={platform.isConnecting}
          disabled={platform.isDemo || !roleArnValid || !settingsValid}
          onClick={onConnect}
        >
          {t("connect")}
        </Button>
        {platform.isDemo && <CloudConnectionDemoHint />}
      </Flex>
      {platform.error && (
        <Alert
          showIcon
          type="error"
          title={platform.error}
          closable={{ onClose: platform.clearError }}
        />
      )}
    </Flex>
  )

  return {
    steps: [
      getCloudConnectionStep(
        t("steps.settingsPrefix"),
        t("steps.settings"),
        <Flex vertical gap={16}>
          <AgentCoreSettingsFields
            value={settings}
            onChange={onSettingsChange}
          />
          <AgentCoreCodeAccessFields
            isUpdate={false}
            value={codeAccess}
            onChange={setCodeAccess}
          />
        </Flex>
      ),
      getCloudConnectionStep(
        tRoot("general.deploy"),
        t("steps.template"),
        <AgentCoreTemplate
          isUpdate={false}
          regions={settings.regions}
          getTemplate={() => template.getTemplate(settings)}
          codeAccess={getStackCodeAccess(codeAccess)}
        />
      ),
      connectStep
    ]
  }
}
